import React, { useState, useRef, useEffect } from 'react';
import { Wrench } from '@phosphor-icons/react';
import { Link } from '../Link';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';
import { CORE_MENU_ITEM, CORE_MENU_ITEM_ACTIVE, CORE_MENU_SURFACE } from '../../constants/corePublicTheme';
import { trackEvent } from '../../utils/analytics/analytics';

export interface Tool {
  route: string;
  label: string;
}

export interface ToolsMenuProps {
  intl: IntlShape;
  currentToolRoute: string;
  tools: Tool[];
  className?: string;
}

/**
 * Tools menu component for navigation
 * Shows tools dropdown on mobile/tablet, ToolSwitcher on desktop
 */
export const ToolsMenu: React.FC<ToolsMenuProps> = ({
  intl,
  currentToolRoute,
  tools,
  className = '',
}) => {
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement>(null);

  // Close tools menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node)) {
        setIsToolsMenuOpen(false);
      }
    };
    if (isToolsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isToolsMenuOpen]);

  const normalizePath = (path: string) => {
    if (path === '/') return path;
    return path.replace(/\/$/, '');
  };

  const normalizedCurrentTool = normalizePath(currentToolRoute);

  return (
    <div ref={toolsMenuRef} className={`2xl:hidden flex-1 flex justify-center relative ${className}`}>
      <button
        onClick={() => {
          const willOpen = !isToolsMenuOpen;
          if (willOpen) {
            trackEvent({ name: 'nav_menu_open', params: { menu: 'tools', locale: intl.locale } });
          }
          setIsToolsMenuOpen(willOpen);
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#3D3529] dark:text-[#C4A882] bg-[#F2EADF] dark:bg-[#2A1D0F] border border-[#DDD5C8] dark:border-[#4A3520] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] transition-colors"
        aria-label={intl.formatMessage({
          id: 'tools.title.aria',
          defaultMessage: 'Tools',
          description: 'Accessibility label for tools button',
        })}
      >
        <Wrench className="w-4 h-4" />
        <span>{intl.formatMessage({ id: 'tools.title.button', defaultMessage: 'Tools', description: 'Button label for tools section' })}</span>
      </button>
      {isToolsMenuOpen && (
        <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-56 py-2 z-50 max-h-[80vh] overflow-y-auto ${CORE_MENU_SURFACE}`}>
          {tools.map((tool) => {
            const normalizedRoute = normalizePath(tool.route);
            const isActive = normalizedCurrentTool === normalizedRoute;
            return (
              <Link
                key={tool.route}
                href={tool.route}
                locale={normalizeSupportedLocale(intl.locale)}
                onClick={() => setIsToolsMenuOpen(false)}
                className={`block px-4 py-2 text-sm transition-colors ${
                  isActive ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
                }`}
              >
                {tool.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
